/**
 * Settings — `/agent-hub/settings`, opened from the header's gear. Home › Agent Hub › Settings.
 *
 * The categories are the user's own — the `category` of their apps in agenthub_master_menu,
 * the same sections and order as Ship Management (useAgentHubApps) — so a category added in
 * Mongo appears here with no code change.
 *
 * Maritime vertical Tabs: "General Settings" shows every category; a category tab shows only
 * its own section. The tab is `?section=<key>` so it survives a reload and can be linked.
 * Each category has an Email and a Teams switch that save on click (useUserSettings).
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Heading, Spinner, Tabs, Text } from '@syia-ai/maritimeai-ui';
import SimpleLayout from '@/components/SimpleLayout';
import { Switch } from '@/components/ui';
import { useSession } from '@/context/SessionContext';
import { useAgentHubApps } from '@/hooks/useAgentHubApps';
import { useUserSettings } from '@/hooks/useUserSettings';
import { categoryKey, channelsFor, type NotificationChannel } from '@/settings/notificationSettings';

const SUBTITLE = 'Manage your account, notifications, and platform preferences.';
const CRUMBS = [{ label: 'Agent Hub', to: '/agent-hub' }, { label: 'Settings' }];

/** The tab value for "all categories". Not a valid category key, so it cannot collide. */
const GENERAL = '__general';

const CHANNEL_LABEL: Record<NotificationChannel, string> = { email: 'Email', teams: 'Teams' };

interface Category {
  key: string;
  label: string;
}

export default function Settings() {
  const { tenant, user } = useSession();
  const { categories: menuCategories, isLoading: menuLoading } = useAgentHubApps(tenant, user?.id);
  const { notifications, state, saveError, setChannel } = useUserSettings(tenant);
  const [params, setParams] = useSearchParams();

  const categories = useMemo<Category[]>(
    () => menuCategories.map((c) => ({ key: categoryKey(c.name), label: c.name })),
    [menuCategories],
  );

  // An unknown or stale ?section= (a category the user no longer has) falls back to General.
  const requested = params.get('section');
  const current = categories.find((c) => c.key === requested)?.key ?? GENERAL;
  const loading = state === 'loading' || menuLoading;

  const select = (value: string) =>
    setParams(value === GENERAL ? {} : { section: value }, { replace: true });

  const renderGroups = (shown: Category[]) => {
    if (loading) return <Spinner size="md" label="Loading your settings" />;
    if (state === 'failed') {
      return (
        <Alert variant="danger" title="Your settings couldn't be loaded">
          Reload the page to try again.
        </Alert>
      );
    }
    if (shown.length === 0) {
      return <Text tone="muted">No agent categories are assigned to your account yet.</Text>;
    }
    return shown.map(({ key, label }) => (
      <section key={key} className="shell-settings__group" aria-label={label}>
        <Heading level={3} size="sm" className="shell-settings__group-title">
          {label}
        </Heading>
        <div className="shell-settings__row">
          <Text as="span" size="sm" className="shell-settings__row-label">
            Notification
          </Text>
          {(Object.keys(CHANNEL_LABEL) as NotificationChannel[]).map((channel) => (
            <Switch
              key={channel}
              checked={channelsFor(notifications, key)[channel]}
              label={`${label} notifications by ${CHANNEL_LABEL[channel]}`}
              onCheckedChange={(next) => void setChannel(key, channel, next)}
            >
              {CHANNEL_LABEL[channel]}
            </Switch>
          ))}
        </div>
      </section>
    ));
  };

  const panels: { value: string; heading: string; shown: Category[] }[] = [
    { value: GENERAL, heading: 'General Settings', shown: categories },
    ...categories.map((c) => ({ value: c.key, heading: c.label, shown: [c] })),
  ];

  return (
    <SimpleLayout currentPath="/agent-hub/settings" crumbs={CRUMBS} subtitle={SUBTITLE} headerTitle="SIYA">
      <Tabs orientation="vertical" value={current} onValueChange={select} className="shell-settings">
        <Tabs.List aria-label="Settings sections" className="shell-settings__nav">
          {panels.map((p) => (
            <Tabs.Tab key={p.value} value={p.value}>
              {p.heading}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {panels.map((p) => (
          <Tabs.Panel key={p.value} value={p.value} className="shell-settings__panel">
            <Heading level={2} size="md" className="shell-settings__title">
              {p.heading}
            </Heading>
            {saveError && <Alert variant="danger">{saveError}</Alert>}
            {/* Only the shown panel renders its switches: a hidden duplicate of every switch
                would still be in the DOM for each category. */}
            {p.value === current && renderGroups(p.shown)}
          </Tabs.Panel>
        ))}
      </Tabs>
    </SimpleLayout>
  );
}
